import { createContext, useContext, useState, useCallback, memo, useEffect } from 'react';

interface Toast {
  id: number;
  message: string;
}

interface ToastContextType {
  showToast: (message: string, duration?: number) => void;
}

const ToastContext = createContext<ToastContextType | null>(null);

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}

const ToastItem = memo<{ toast: Toast; onRemove: (id: number) => void; duration: number }>(
  function ToastItem({ toast, onRemove, duration }) {
    useEffect(() => {
      const timer = setTimeout(() => {
        onRemove(toast.id);
      }, duration);
      return () => clearTimeout(timer);
    }, [toast.id, onRemove, duration]);

    return (
      <div
        role="alert"
        aria-live="polite"
        className="bg-slate-800 text-white px-6 py-4 rounded-xl shadow-2xl border border-slate-700 animate-slide-up flex items-center gap-3 max-w-sm"
      >
        <svg className="w-5 h-5 text-indigo-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <span className="text-sm">{toast.message}</span>
      </div>
    );
  }
);

export const ToastProvider = memo<{ children: React.ReactNode }>(function ToastProvider({ children }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [defaultDuration, setDefaultDuration] = useState(3000);

  const showToast = useCallback((message: string, duration: number = 3000) => {
    const id = Date.now();
    setDefaultDuration(duration);
    setToasts((prev) => [...prev, { id, message }]);
  }, []);

  const removeToast = useCallback((id: number) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      {/* Toast Container */}
      <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[9999] flex flex-col gap-2 pointer-events-none">
        {toasts.map((toast) => (
          <ToastItem key={toast.id} toast={toast} onRemove={removeToast} duration={defaultDuration} />
        ))}
      </div>
    </ToastContext.Provider>
  );
});
